import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { SpotInterface } from 'types';
import { downloadClient } from 'clients';
import { getReportRentalTypeOptions, RentalTypeFilterValue, TICKET_RENTAL_TYPE } from 'utils/constants';
import { dateToUnix, getFormattedDateYYYY_MM_DD, getMonthDateRange } from 'utils/helpers';
import DownloadIcon from 'assets/icons/download.svg?react';
import LinesIcon from 'assets/icons/lines.svg?react';
import Modal, { ModalRef } from 'components/Modal/Modal';
import Button from 'components/Button/Button';
import AGSimpleSelect from 'components/AGSelect/AGSimpleSelect/AGSimpleSelect';
import DateSelector from 'components/IncomeStatement/DateSelector';
import styles from './DownloadReportModal.module.css';
import WarningBox from 'components/WarningBox/WarningBox';

type DownloadReportModalProps = {
    spot: SpotInterface;
    rentalType: RentalTypeFilterValue;
    month: number;
    year: number;
    // Whether this spot shares ticket revenue with the owner; gates the "Violations" option.
    hasTicketRevenue?: boolean;
};

function DownloadReportModal({
    spot,
    month,
    year,
    rentalType: initialRentalType,
    hasTicketRevenue,
}: DownloadReportModalProps) {
    const [startDate, setStartDate] = useState<Date>(new Date());
    const [endDate, setEndDate] = useState<Date>(new Date());

    const [rentalType, setRentalType] = useState<RentalTypeFilterValue>(initialRentalType);
    const [isDownloading, setIsDownloading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const downloadModalRef = useRef<ModalRef>();

    useEffect(() => {
        const { startDate, endDate } = getMonthDateRange(month, year, false);
        setStartDate(startDate);
        setEndDate(endDate);
    }, [month, year]);

    useEffect(() => {
        setRentalType(initialRentalType);
    }, [initialRentalType]);

    const rentalTypeOptions = useMemo(() => getReportRentalTypeOptions(hasTicketRevenue), [hasTicketRevenue]);

    // Never let a stale "Violations" selection survive into the download request for a spot
    // that doesn't share ticket revenue.
    const selectedRentalType = !hasTicketRevenue && rentalType === TICKET_RENTAL_TYPE ? '' : rentalType;

    async function downloadReport() {
        setIsDownloading(true);
        setErrorMessage('');
        try {
            let filtersUrl = `?start_date=${dateToUnix(startDate)}&end_date=${dateToUnix(endDate)}`;
            if (selectedRentalType) filtersUrl += `&rental_type=${selectedRentalType}`;

            const startDateFormatted = getFormattedDateYYYY_MM_DD(startDate);
            const endDateFormatted = getFormattedDateYYYY_MM_DD(endDate);
            const fileName = `revenue_reports_${
                selectedRentalType || 'all'
            }_${startDateFormatted}_${endDateFormatted}.csv`;

            await downloadClient.downloadDailyReports(spot.uuid, filtersUrl, fileName);
        } catch (e) {
            setErrorMessage((e as Error).message || e?.toString() || 'Could not download reports');
        } finally {
            setIsDownloading(false);
        }
    }

    const handleSaveDateRange = useCallback((startDate: Date, endDate: Date) => {
        setStartDate(startDate);
        setEndDate(endDate);
    }, []);

    return (
        <>
            <Button size="sm" onClick={() => downloadModalRef?.current?.openModal()}>
                <DownloadIcon />
                Download
            </Button>

            <Modal ref={downloadModalRef} title="Generate Report">
                <div className={styles.container}>
                    {errorMessage && <WarningBox type="error">{errorMessage}</WarningBox>}
                    <p>
                        Get a CSV file containing count of rentals, gross revenue, count of refunds, refund amount,
                        adjusted revenue, AirGarage commission, and net revenue by day.
                    </p>

                    <div>
                        <p>Rental Type</p>

                        <AGSimpleSelect
                            placeholder="Filter rental type"
                            selected={selectedRentalType}
                            onSelect={(s) => setRentalType(s as RentalTypeFilterValue)}
                            options={rentalTypeOptions}
                            icon={<LinesIcon />}
                            maxHeight="140px"
                            width="100%"
                            disabled={isDownloading}
                        />
                    </div>
                    <div>
                        <p>Date Range</p>
                        <DateSelector
                            startDate={startDate}
                            endDate={endDate}
                            onSave={handleSaveDateRange}
                            dropdownWidth="100%"
                            isInModal
                            disabled={isDownloading}
                        />
                    </div>

                    <Button disabled={isDownloading} size="md" onClick={downloadReport}>
                        <DownloadIcon />
                        Download Report
                    </Button>
                </div>
            </Modal>
        </>
    );
}

export default DownloadReportModal;
